import { ContactPersonId, StationId } from '@app/icare/db/schema/common'
import { PatientId } from '@app/icare/entities/patient/schema/Patient'
import { PatientAddressId } from '@app/icare/entities/patient/schema/PatientAddress'
import { PatientCareManagerId } from '@app/icare/entities/patient/schema/PatientCareManager'
import { PatientCareServiceId } from '@app/icare/entities/patient/schema/PatientCareService'
import { PatientClinicId } from '@app/icare/entities/patient/schema/PatientClinic'
import { PatientCooperationPartnerId } from '@app/icare/entities/patient/schema/PatientCooperationPartner'
import { PatientCostCarrierId } from '@app/icare/entities/patient/schema/PatientCostCarrier'
import { PatientDoctorId } from '@app/icare/entities/patient/schema/PatientDoctor'
import { PatientMedicalTechnologyId } from '@app/icare/entities/patient/schema/PatientMedicalTechnology'
import { PatientPharmacyId } from '@app/icare/entities/patient/schema/PatientPharmacy'
import { PatientRelativeId } from '@app/icare/entities/patient/schema/PatientRelative'
import { PatientSeparateResidenceId } from '@app/icare/entities/patient/schema/PatientSeparateResidence'
import { PatientTherapyId } from '@app/icare/entities/patient/schema/PatientTherapy'
import { msg } from '@lingui/core/macro'
import { defineEnum } from '@pkg/shared/utils/enum-builder'
import { z } from 'zod'
import { PatientFormCommonSchema } from './PatientFormCommon'
import { PatientFormTypeEnum } from './PatientFormType'

export const PatientMasterDataSheetStatusEnum = defineEnum({
  new: msg`Neu`,
  send: msg`Gesendet`,
  cancelled: msg`Abgebrochen`,
})

export type PatientMasterDataSheetStatusType = typeof PatientMasterDataSheetStatusEnum.$.Type

export const PatientMasterDataSheetSchema = PatientFormCommonSchema.extend({
  status: z.enum(PatientMasterDataSheetStatusEnum.$.zodEnum()).default(PatientMasterDataSheetStatusEnum.new),
  statusChangedAt: z.string().datetime().nullish().default(null),
  type: z.literal(PatientFormTypeEnum.MasterDataSheet).default(PatientFormTypeEnum.MasterDataSheet),
  form: z.object({
    recipient: z
      .object({
        source: z.union([
          PatientCareServiceId.ref,
          PatientPharmacyId.ref,
          PatientCooperationPartnerId.ref,
          z.literal('other'),
        ]),
        other: z.string().nullish(),
      })
      .refine(r => {
        if (!r) {
          return false
        }
        if (r.source && r.source !== 'other') {
          return true
        }
        if (r.source === 'other') {
          return !!r.other && r.other.trim().length > 0
        }
        return false
      }),
    addresses: z
      .array(
        z.object({
          source: z.union([PatientId.ref, PatientAddressId.ref, PatientSeparateResidenceId.ref]),
          variant: z.enum(['delivery', 'billing']).nullish(),
        }),
      )
      .default([]),
    therapies: z
      .array(
        z.object({
          source: PatientTherapyId.ref,
        }),
      )
      .default([]),
    costCarriers: z
      .array(
        z.object({
          source: PatientCostCarrierId.ref,
        }),
      )
      .default([]),
    relatives: z
      .array(
        z.object({
          source: PatientRelativeId.ref,
        }),
      )
      .default([]),
    prescribers: z
      .array(
        z.object({
          source: z.union([PatientClinicId.ref, PatientDoctorId.ref]),
          contact: z.union([StationId.ref, ContactPersonId.ref, z.string()]).nullish(),
          comment: z
            .string() /* .max(600).refine((value) => {
        const lines = value.split('\n')
        if (lines.length > 3) {
          return false
        }
        for (const line of lines) {
          if (line.length > 150) {
            return false
          }
        }
        return true
      }, 'Es sind nur 3 Zeilen erlaubt') */
            .nullish(),
        }),
      )
      .default([])
      .superRefine((data, _ctx) => {
        if (!data || data.length === 0) {
          return
        }
        data.forEach(_item => {
          // if (isTable(item.source, 'patientClinic') && (item.contacts?.length ?? 0) === 0) {
          //   ctx.addIssue({
          //     code: z.ZodIssueCode.custom,
          //     path: ['contacts'],
          //     message: 'Für Kliniken müssen mindestens ein Kontakt angegeben werden.',
          //   })
          // }
        })
      }),
    careServices: z
      .array(
        z.object({
          source: PatientCareServiceId.ref,
        }),
      )
      .default([]),
    pharmacies: z
      .array(
        z.object({
          source: PatientPharmacyId.ref,
          contact: ContactPersonId.ref.nullish(),
        }),
      )
      .default([]),
    medicalTechnologies: z
      .array(
        z.object({
          source: PatientMedicalTechnologyId.ref,
        }),
      )
      .default([]),
    careManagers: z
      .array(
        z.object({
          source: PatientCareManagerId.ref,
        }),
      )
      .default([]),
    comment: z
      .string() /* .max(600).refine((value) => {
      const lines = value.split('\n')
      if (lines.length > 4) {
        return false
      }
      for (const line of lines) {
        if (line.length > 150) {
          return false
        }
      }
      return true
    }) */
      .nullish(),
  }),
})

export type PatientMasterDataSheet = z.infer<typeof PatientMasterDataSheetSchema>
